import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import axios from 'axios';
import './FamilyStyles.css';

/**
 * Family Video Call Component
 * WebRTC-Videoanruf von Family User zu Senior Device
 */
function FamilyVideoCall() {
  const { seniorId } = useParams();
  const { familyUser } = useFamilyAuth();
  const navigate = useNavigate();
  
  // Check for Drop-In parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isDropIn = urlParams.get('dropin') === 'true';
  
  const [senior, setSenior] = useState(null);
  const [device, setDevice] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, dialing, ringing, connecting, in_call, ended, error
  const [error, setError] = useState(null);
  const [currentCallSession, setCurrentCallSession] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState('good'); // good, fair, poor
  const [cameraError, setCameraError] = useState(null);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const sessionRef = useRef(null); // Ref für session, damit Event-Handler darauf zugreifen können
  const callTimerRef = useRef(null);
  const containerRef = useRef(null);
  const ringTimeoutRef = useRef(null); // Timeout für "keine Antwort"
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  useEffect(() => {
    loadSeniorAndDevice();
    return () => cleanup();
  }, [seniorId]);

  // Call-Timer starten wenn Call verbunden ist
  useEffect(() => {
    if (callState === 'in_call') {
      setCallDuration(0);
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const loadSeniorAndDevice = async () => {
    try {
      // Senior laden
      const seniorsRes = await axios.get(
        `${BACKEND_URL}/api/households/${familyUser.household_id}/seniors`
      );
      const seniorData = seniorsRes.data.find(s => s.id === seniorId);
      setSenior(seniorData);

      // Device laden
      const devicesRes = await axios.get(
        `${BACKEND_URL}/api/households/${familyUser.household_id}/devices`
      );
      const deviceData = devicesRes.data.find(d => d.senior_id === seniorId);
      
      if (!deviceData) {
        setError('Kein Gerät für diesen Senior gefunden');
        setCallState('error');
        return;
      }
      
      if (deviceData.status !== 'online') {
        setError('Das Gerät ist offline');
        setCallState('error');
        return;
      }
      
      setDevice(deviceData);
      
      // Automatisch Anruf starten
      startCall(deviceData);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      setError('Fehler beim Laden der Daten');
      setCallState('error');
    }
  };

  const startCall = async (deviceData) => {
    try {
      setCallState('dialing');
      
      // WebSocket verbinden ZUERST (Medien werden erst angefordert, wenn Call akzeptiert wird)
      await connectWebSocket(deviceData);
    } catch (error) {
      console.error('Fehler beim Starten des Anrufs:', error);
      setError('Anruf konnte nicht gestartet werden');
      setCallState('error');
    }
  };

  const connectWebSocket = (deviceData) => {
    return new Promise((resolve, reject) => {
      // Cleanup alte WebSocket-Verbindung falls vorhanden
      if (wsRef.current) {
        console.log('🔌 Schließe alte WebSocket-Verbindung');
        wsRef.current.close();
        wsRef.current = null;
      }
      
      const ws = new WebSocket(`${WS_URL}/api/ws/call/family/${familyUser.id}`);
      
      ws.onopen = () => {
        console.log('Family WebSocket verbunden');
        
        // Call starten via WebSocket
        ws.send(JSON.stringify({
          type: 'call_start',
          device_id: deviceData.id,
          call_type: isDropIn ? 'dropin' : 'standard'
        }));
        
        wsRef.current = ws;
        resolve();
      };
      
      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        console.log('Family empfängt:', data);
        
        switch (data.type) {
          case 'call_started':
            const session = { id: data.session_id };
            setCurrentCallSession(session);
            sessionRef.current = session; // Ref auch setzen
            setCallState('ringing');
            
            // Timeout: Keine Antwort nach 60 Sekunden
            ringTimeoutRef.current = setTimeout(() => {
              if (callState === 'ringing') {
                setError('Keine Antwort vom Gerät');
                setCallState('ended');
                cleanup();
              }
            }, 60000);
            break;
            
          case 'call_accepted':
            // Timeout clearen, da Call akzeptiert wurde
            if (ringTimeoutRef.current) {
              clearTimeout(ringTimeoutRef.current);
            }
            
            setCallState('connecting');
            // Jetzt Kamera/Mikrofon anfordern und WebRTC setup
            try {
              await setupLocalMedia();
              setupPeerConnection(); // PeerConnection mit lokalen Tracks erstellen
              // Offer erstellen und senden
              await createAndSendOffer(data.session_id);
            } catch (error) {
              console.error('Fehler beim Einrichten der Medien:', error);
              const errorMsg = error.name === 'NotAllowedError' 
                ? 'Kamera-/Mikrofonzugriff verweigert. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.'
                : error.name === 'NotFoundError'
                ? 'Keine Kamera oder Mikrofon gefunden. Bitte prüfe deine Geräte.'
                : 'Fehler beim Zugriff auf Kamera/Mikrofon';
              
              setCameraError(errorMsg);
              setError(errorMsg);
              setCallState('error');
              // Call beenden
              if (wsRef.current && sessionRef.current) {
                wsRef.current.send(JSON.stringify({
                  type: 'call_end',
                  session_id: sessionRef.current.id
                }));
              }
            }
            break;
            
          case 'call_rejected':
            setCallState('ended');
            setError('Anruf wurde abgelehnt');
            setTimeout(() => navigate(-1), 3000);
            break;
            
          case 'sdp_answer':
            console.log('📡 Empfangene SDP Answer:', { 
              hasPeerConnection: !!pcRef.current, 
              hasAnswer: !!data.sdp_answer,
              answerValue: data.sdp_answer ? 'vorhanden' : 'NULL/undefined',
              fullData: data
            });
            if (pcRef.current && data.sdp_answer) {
              await pcRef.current.setRemoteDescription(
                new RTCSessionDescription({ type: 'answer', sdp: data.sdp_answer })
              );
              console.log('✅ Remote description (answer) gesetzt');
              setCallState('in_call');
            } else {
              console.error('❌ SDP Answer Problem:', {
                noPeerConnection: !pcRef.current,
                noAnswer: !data.sdp_answer
              });
              if (!data.sdp_answer) {
                setError('Keine Antwort vom Gerät erhalten');
                setCallState('error');
              }
            }
            break;
            
          case 'ice_candidate':
            if (pcRef.current && data.candidate) {
              await pcRef.current.addIceCandidate(
                new RTCIceCandidate(data.candidate)
              );
            }
            break;
            
          case 'call_end':
            setCallState('ended');
            setTimeout(() => navigate(-1), 2000);
            break;
            
          default:
            console.log('Unbekannter Message-Typ:', data.type);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket Fehler:', error);
        setError('Verbindungsfehler');
        setCallState('error');
        reject(error);
      };
      
      ws.onclose = () => {
        console.log('Family WebSocket geschlossen');
      };
    });
  };

  const setupLocalMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Kamera-/Mikrofonzugriff verweigert:', error);
      setError('Kamera- oder Mikrofonzugriff verweigert. Bitte Berechtigungen prüfen.');
      setCallState('error');
      throw error;
    }
  };

  const setupPeerConnection = () => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });
    
    // Lokale Tracks hinzufügen
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    // ICE Candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current && sessionRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          session_id: sessionRef.current.id,
          candidate: event.candidate
        }));
      }
    };
    
    // Remote Stream
    pc.ontrack = (event) => {
      console.log('Remote Track empfangen:', event.streams[0]);
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log('ICE Connection State:', pc.iceConnectionState);
      
      // Connection Quality basierend auf ICE State
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setCallState('in_call');
        setConnectionQuality('good');
      } else if (pc.iceConnectionState === 'checking') {
        setConnectionQuality('fair');
      } else if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        setConnectionQuality('poor');
        setError('Verbindung unterbrochen');
        setCallState('error');
      }
    };
    
    pcRef.current = pc;
  };

  const createAndSendOffer = async (sessionId) => {
    if (!pcRef.current) return;
    
    try {
      const offer = await pcRef.current.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: true
      });
      
      await pcRef.current.setLocalDescription(offer);
      
      if (wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'sdp_offer',
          session_id: sessionId || sessionRef.current?.id,
          sdp_offer: offer.sdp
        }));
      }
    } catch (error) {
      console.error('Fehler beim Erstellen des Offers:', error);
      setError('WebRTC-Verbindung fehlgeschlagen');
      setCallState('error');
    }
  };

  const endCall = () => {
    if (wsRef.current && sessionRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'call_end',
        session_id: sessionRef.current.id
      }));
    }
    
    cleanup();
    setCallState('ended');
    setTimeout(() => navigate(-1), 1000);
  };

  // UI Control Functions
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const toggleFullscreen = () => {
    // iOS Safari unterstützt requestFullscreen nicht, verwende Video-Element stattdessen
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS && remoteVideoRef.current) {
      // Auf iOS: Video-Element in Fullscreen
      if (remoteVideoRef.current.webkitEnterFullscreen) {
        remoteVideoRef.current.webkitEnterFullscreen();
      }
    } else {
      // Auf anderen Browsern: Container in Fullscreen
      if (!document.fullscreenElement) {
        if (containerRef.current?.requestFullscreen) {
          containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        } else if (containerRef.current?.webkitRequestFullscreen) {
          containerRef.current.webkitRequestFullscreen();
          setIsFullscreen(true);
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    }
  };

  const cleanup = () => {
    // Call-Timer stoppen
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    
    // Ring-Timeout clearen
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    
    // Tracks stoppen
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // PeerConnection schließen
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    // WebSocket schließen
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  // UI Rendering
  return (
    <div className="family-video-call">
      {/* Fehler-Overlay */}
      {callState === 'error' && (
        <div className="family-call-error">
          <div className="family-call-error-content">
            <div className="family-call-error-icon">❌</div>
            <h2>Fehler</h2>
            <p>{error}</p>
            <button 
              onClick={() => navigate(-1)}
              className="family-btn-primary"
            >
              Zurück
            </button>
          </div>
        </div>
      )}

      {/* Dialing/Ringing State */}
      {(callState === 'dialing' || callState === 'ringing') && (
        <div className="family-call-ringing">
          <div className="family-call-ringing-content">
            {senior?.image_url ? (
              <img src={senior.image_url} alt={senior?.name} className="family-call-avatar" />
            ) : (
              <div className="family-call-avatar-placeholder">
                {senior?.name?.charAt(0)}
              </div>
            )}
            <h2>{senior?.name}</h2>
            <p className="family-call-status">
              {callState === 'dialing' && 'Verbinde...'}
              {callState === 'ringing' && (isDropIn ? 'Drop-In wird gestartet...' : 'Klingelt...')}
            </p>
            {isDropIn && callState === 'ringing' && (
              <p className="family-call-dropin-info">
                🚪 Auto-Annahme in 3 Sekunden
              </p>
            )}
            <div className="family-call-ringing-animation">
              <div className="family-call-ring"></div>
              <div className="family-call-ring"></div>
              <div className="family-call-ring"></div>
            </div>
            <button 
              onClick={endCall}
              className="family-btn-hang-up"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* In-Call State */}
      {(callState === 'connecting' || callState === 'in_call') && (
        <div className="family-call-active" ref={containerRef}>
          {/* Remote Video (Groß) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="family-call-remote-video"
          />
          
          {/* Local Video (Klein, Overlay) */}
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="family-call-local-video"
            style={{ display: isVideoOff ? 'none' : 'block' }}
          />
          
          {/* Video Off Placeholder */}
          {isVideoOff && (
            <div className="family-call-local-video-off">
              <div className="family-video-off-icon">📹</div>
            </div>
          )}
          
          {/* Call Info Header */}
          <div className="family-call-header">
            <div className="family-call-info">
              <span className="family-call-name">{senior?.name}</span>
              <span className="family-call-status-text">
                {callState === 'connecting' ? 'Verbinde...' : `${Math.floor(callDuration / 60)}:${String(callDuration % 60).padStart(2, '0')}`}
              </span>
            </div>
            {callState === 'in_call' && (
              <div className={`family-connection-indicator family-connection-${connectionQuality}`}>
                <div className="connection-dot"></div>
                {connectionQuality === 'good' && '●●●'}
                {connectionQuality === 'fair' && '●●○'}
                {connectionQuality === 'poor' && '●○○'}
              </div>
            )}
          </div>
          
          {/* Call Controls */}
          <div className="family-call-controls">
            <button 
              onClick={toggleMute}
              className={`family-control-btn ${isMuted ? 'active' : ''}`}
              title={isMuted ? 'Stummschaltung aufheben' : 'Stummschalten'}
            >
              {isMuted ? '🔇' : '🎤'}
            </button>
            
            <button 
              onClick={toggleVideo}
              className={`family-control-btn ${isVideoOff ? 'active' : ''}`}
              title={isVideoOff ? 'Video einschalten' : 'Video ausschalten'}
            >
              {isVideoOff ? '📹' : '📷'}
            </button>
            
            <button 
              onClick={endCall}
              className="family-btn-hang-up-active"
            >
              📞
            </button>
            
            <button 
              onClick={toggleFullscreen}
              className="family-control-btn"
              title={isFullscreen ? 'Vollbild verlassen' : 'Vollbild'}
            >
              {isFullscreen ? '⛶' : '⛶'}
            </button>
          </div>
        </div>
      )}

      {/* Ended State */}
      {callState === 'ended' && (
        <div className="family-call-ended">
          <div className="family-call-ended-content">
            <div className="family-call-ended-icon">✓</div>
            <h2>Anruf beendet</h2>
            <p>Wird geschlossen...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default FamilyVideoCall;
